// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, IIconStyles, Icon, Link, TextField, TooltipDelay, TooltipHost } from "@fluentui/react";
import { dgFontFamily, userFontFamily } from "components/common/Fonts";
import { evalMetricsHumanColor, primaryTheme } from "components/shell/presentation/AppStyles";
import { NavigationType, ReportViewMode, useNavigationService } from 'hooks/navigationService';
import { ReactNode, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { IMessageFeedback } from "./ChatView";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { Citation, CitationModel, CitationType, isCitationV2 } from "store/chat/chatApi";
import { useDgIcons, dgIconNames } from '../../hooks/dgIcons';
import { Telemetry } from "telemetry/TelemetryManager";
import { ChatCitationClickedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";


const UserMessageCss = css`
    background-color: ${`${evalMetricsHumanColor}`};
    align-self: flex-end;
`;

const ChatMessageRoot = styled.div<{ self?: boolean }>`
    display: flex;
    flex-direction: column;
    width: fit-content;
    height: fit-content;
    max-width: 75%;
    background-color: ${primaryTheme.palette.white};
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
    border-radius: 10px;

    font-family: ${userFontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    text-align: left;
    white-space: pre-wrap;

    padding: 10px;
    box-sizing: border-box;

    ${props => !!props.self && UserMessageCss}
`;

const MessageContainer = styled.span`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

interface ChatMessageProps {
    children?: ReactNode;
    footer?: ReactNode;
    isUser?: boolean;
}

export const ChatMessage = (props: ChatMessageProps): JSX.Element => {
    return (
        <ChatMessageRoot self={props.isUser}>
            <MessageContainer>{props.children}</MessageContainer>
            {props.footer}
        </ChatMessageRoot>
    );
}

const CitationsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    padding-right: 10px;
`;

const CitationIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MessageFooterLink = styled(Link) <{ hidden?: boolean }>`
    display: flex;
    flex-direction: row;
    border: none;
    background: none;
    padding: 0;
    gap: 3px;
    align-items: center;
    cursor: pointer;
    align-self: flex-end;

    font-family: ${dgFontFamily};
    font-size: 12px;
    white-space: nowrap;

    ${props => !!props.hidden && 'display: none'};
`;


const FeedbackContainer = styled.div<{ hidden?: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-self: flex-end;
    ${props => !!props.hidden && 'display: none'};
`;

const FooterRoot = styled.div`
    display: flex;
    flex-direction: row;
    padding-top: 10px;
    align-items: center;
    justify-content: flex-end;
`;

interface SystemChatMessageFooterProps {
    citations: Citation[];
    docSetId: string;
    feedback: IMessageFeedback;
    onGiveFeedback?: (feedback: boolean, comment?: string) => void;
}

export const SystemChatMessageFooter = (props: SystemChatMessageFooterProps): JSX.Element => {
    const [showFeedbackCommentModal, setShowFeedbackCommentModal] = useState(false);
    const [comment, setComment] = useState("");

    const { enabled: feedbackEnabled, feedback, comment: existingFeedbackComment } = props.feedback;
    const dgIcons = useDgIcons();
    const navService = useNavigationService();

    const visibleCitations = useMemo(() => props.citations.filter(x => !isCitationV2(x)) as CitationModel[], [props.citations]);

    useEffect(() => {
        if (!showFeedbackCommentModal) {
            setComment('');
        }
    }, [showFeedbackCommentModal]);

    function addComment(comment: string) {
        if (feedback !== undefined && !!comment.trim()) {
            props.onGiveFeedback?.(feedback, comment);
        }
        setShowFeedbackCommentModal(false);
    }

    function handleCitationClick(citation: CitationModel) {
        Telemetry.trackEvent<ChatCitationClickedSchema>({
            citation: citation,
            eventName: TelemetryEvent.ChatCitationClicked
        });

        if (!!citation.documentId) {
            navService.navToReportPage(
                NavigationType.PushNewPage,
                {
                    mode: ReportViewMode.Document,
                    documentId: props.docSetId + ":" + citation.documentId,
                });
        }
        else if (!!citation.reportQuery) {
            navService.navToReportPage(
                NavigationType.PushNewPage,
                {
                    mode: ReportViewMode.Table
                });
        }
    }

    function renderCitation(citation: CitationModel, index: number) {
        return (
            <CitationIcon key={`citation-${index}`} onClick={() => handleCitationClick(citation)}>
                {citation.citationType === CitationType.Document ?
                    dgIcons.icon(dgIconNames.DgPageNoWriting, citation.label, 22, 18, primaryTheme.palette.blackTranslucent40, '') :
                    dgIcons.icon(dgIconNames.DgTableLayout, citation.label, 22, 18, primaryTheme.palette.blackTranslucent40, '')
                }
            </CitationIcon>
        )
    }

    return (
        <>
            <FooterRoot>
                <FeedbackContainer hidden={!feedbackEnabled}>
                    <FeedbackIcon feedbackValue={true} readOnly={!props.onGiveFeedback} currentFeedback={feedback} onGiveFeedback={props.onGiveFeedback} />
                    <FeedbackIcon feedbackValue={false} readOnly={!props.onGiveFeedback}  currentFeedback={feedback} onGiveFeedback={(feedback) => { props.onGiveFeedback?.(feedback); setShowFeedbackCommentModal(true) }} />
                    <MessageFooterLink
                        title="Give additional feedback"
                        hidden={feedback === undefined || !!existingFeedbackComment || showFeedbackCommentModal || !props.onGiveFeedback}
                        onClick={() => setShowFeedbackCommentModal(true)}><i>Tell us more...</i></MessageFooterLink>
                </FeedbackContainer>
                {visibleCitations.length > 0 && (
                    <CitationsContainer>
                        <span>{visibleCitations.length > 1 ? "Citations:" : "Citation:"}</span>
                        {visibleCitations.map(renderCitation)}
                    </CitationsContainer>
                )}
            </FooterRoot>
            <ConfirmationDialog
                hidden={!showFeedbackCommentModal}
                title="Give Additional Feedback"
                confirmButtonText="Submit"
                dismissButtonText="Cancel"
                dismissedCallback={() => setShowFeedbackCommentModal(false)}
                confirmedCallback={() => addComment(comment)}
                disabled={!comment.trim()}
                styles={{
                    main: {
                        selectors: {
                            '@media (min-width: 480px)': {
                                width: 600,
                                maxWidth: '600px'
                            }
                        }
                    }
                }} >
                <TextField placeholder="Enter feedback here..." onChange={(ev, value) => setComment(value ?? '')} multiline rows={8} maxLength={500} styles={{ field: { maxHeight: "400px" } }} />
            </ConfirmationDialog>
        </>
    )
}

interface FeedbackIconProps {
    feedbackValue: boolean;
    currentFeedback?: boolean;
    onGiveFeedback?: (feedback: boolean) => void;
    readOnly?: boolean;
}

const FeedbackIcon = (props: FeedbackIconProps): JSX.Element => {
    const isSelected = props.feedbackValue === props.currentFeedback;
    const isActive = !isSelected && !props.readOnly;

    const styles: Partial<IIconStyles> = ({
        root: {
            color: "black",
            cursor: "default"
        }
    })

    const activeStyles: Partial<IIconStyles> = ({
        root: {
            cursor: "pointer",
            ":hover": {
                transform: "scale(1.15)",
                color: primaryTheme.palette.themePrimary
            }
        }
    })

    function getIcon() {
        if (props.feedbackValue) {
            return isSelected ? "LikeSolid" : "Like";
        } else {
            return isSelected ? "DislikeSolid" : "Dislike";
        }
    }

    const title = props.feedbackValue ? "Correct response" : "Incorrect response";

    return (
        <TooltipHost
            content={title}
            directionalHint={DirectionalHint.bottomCenter}
            delay={TooltipDelay.zero}
            styles={{ root: { lineHeight: "10px" } }}>
            <Icon
                iconName={getIcon()}
                aria-label={title}
                styles={isActive ? { ...styles, ...activeStyles } : styles}
                onClick={() => isActive ? props.onGiveFeedback?.(props.feedbackValue) : {}} />
        </TooltipHost>
    )
}

const AnimatedEllipses = styled.div`
    font-size: 16px;
    display: flex;
    gap: 0.5em;
    font-weight: 900;

    span {
        display: inline-block;
        animation-name: bouncing;
        animation-duration: 1000ms;
        animation-iteration-count: infinite;
        animation-timing-function: ease-out;
        &:nth-child(2) {
            animation-delay: 200ms;
        }
        &:nth-child(3) {
            animation-delay: 400ms;
        }
    }

    @keyframes bouncing {
        0% {
            transform: none;
        }
        33% {
            transform: translateY(-.5em);
        }
        66% {
            transform: none;
        }
    }
`;

export const LoadingChatMessage = (): JSX.Element => {
    return (
        <ChatMessage key='loading'>
            <AnimatedEllipses>
                <span>.</span>
                <span>.</span>
                <span>.</span>
            </AnimatedEllipses>
        </ChatMessage>
    )
}
